import { useEffect, useMemo, useRef, useState } from "react";
import { BadgeCheck, CheckCircle2, Flag, MessageCircle, ThumbsUp, Trash2, UserRound } from "lucide-react";
import { channelApi, BACKEND, errText } from "@/lib/api";

const timestamp = (value) => value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "Just now";
const restrictionTime = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "an unknown time UTC" : `${date.toLocaleString(undefined, { dateStyle: "long", timeStyle: "short", timeZone: "UTC" })} UTC`;
};
const reportCategories = [
  ["spam", "Spam or misleading content"],
  ["harassment", "Harassment or bullying"],
  ["hate_speech", "Hate speech or discrimination"],
  ["sexual_content", "Sexual content"],
  ["violence", "Violence or threats"],
  ["misinformation", "Misinformation"],
  ["other", "Something else"],
];

const Avatar = ({ mediaId, name, size = "h-10 w-10" }) => (
  <div className={`${size} flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/10 text-white/55`}>
    {mediaId ? <img src={`${BACKEND}/api/media/${mediaId}/file`} alt="" className="h-full w-full object-cover" /> : <UserRound className="h-5 w-5" aria-label={`${name || "Viewer"} profile photo`} />}
  </div>
);

const VerifiedCheck = () => <BadgeCheck className="h-4 w-4 shrink-0 fill-sky-500 text-white" aria-label="Verified creator" />;
const CreatorBadge = () => <span className="inline-flex items-center gap-1 text-xs font-semibold text-white/75"><VerifiedCheck /><span>Creator</span></span>;

export const ChannelEngagement = ({ showId, customer }) => {
  const [likes, setLikes] = useState({ liked: false, likes_count: 0 });
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [replyText, setReplyText] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [mentionOptions, setMentionOptions] = useState([]);
  const [mentionIds, setMentionIds] = useState([]);
  const mentionRequest = useRef(0);
  const [expanded, setExpanded] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [reportTarget, setReportTarget] = useState(null);
  const [reportCategory, setReportCategory] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [restrictionClock, setRestrictionClock] = useState(Date.now());
  const commentRestriction = customer?.comment_restriction;

  useEffect(() => {
    if (commentRestriction?.type !== "temporary") return undefined;
    const endsAt = Date.parse(commentRestriction.ends_at);
    if (!Number.isFinite(endsAt)) return undefined;
    const timeout = window.setTimeout(() => setRestrictionClock(Date.now()), Math.max(0, endsAt - Date.now()));
    return () => window.clearTimeout(timeout);
  }, [commentRestriction?.ends_at, commentRestriction?.type]);

  const restrictionEndsAt = Date.parse(commentRestriction?.ends_at);
  const commentRestrictionActive = commentRestriction?.type === "permanent"
    || (commentRestriction?.type === "temporary" && (!Number.isFinite(restrictionEndsAt) || restrictionEndsAt > restrictionClock));

  useEffect(() => {
    let active = true;
    setLikes({ liked: false, likes_count: 0 });
    setComments([]);
    setExpanded(new Set());
    setError("");
    Promise.all([
      channelApi.get(`/customer/shows/${showId}/engagement`),
      channelApi.get(`/customer/shows/${showId}/comments`),
    ]).then(([engagement, response]) => {
      if (!active) return;
      setLikes(engagement.data);
      setComments(Array.isArray(response.data) ? response.data : []);
    }).catch((e) => { if (active) setError(errText(e)); });
    return () => { active = false; };
  }, [showId]);

  const roots = useMemo(() => comments
    .filter((comment) => !comment.parent_id)
    .sort((left, right) => new Date(right.created_at) - new Date(left.created_at)), [comments]);
  const repliesByParent = useMemo(() => comments.reduce((result, comment) => {
    if (comment.parent_id) result[comment.parent_id] = [...(result[comment.parent_id] || []), comment];
    return result;
  }, {}), [comments]);

  const updateDraft = (value, parentId = null) => {
    if (parentId) setReplyText(value);
    else setCommentText(value);
    setMentionIds((current) => current.filter((mention) => value.includes(`@${mention.username}`)));
    const match = value.match(/(?:^|\s)@([^\s@]{0,50})$/);
    if (!match) {
      mentionRequest.current += 1;
      setMentionOptions([]);
      return;
    }
    const requestId = ++mentionRequest.current;
    channelApi.get("/customer/mentions", { params: { q: match[1] } }).then(({ data }) => {
      if (requestId === mentionRequest.current) setMentionOptions(Array.isArray(data) ? data : []);
    }).catch(() => {
      if (requestId === mentionRequest.current) setMentionOptions([]);
    });
  };

  const insertMention = (account, parentId = null) => {
    const value = parentId ? replyText : commentText;
    const atIndex = value.lastIndexOf("@");
    const nextValue = `${value.slice(0, atIndex)}@${account.username} `;
    if (parentId) setReplyText(nextValue);
    else setCommentText(nextValue);
    setMentionIds((current) => [...current.filter((mention) => mention.id !== account.id), { id: account.id, username: account.username }]);
    setMentionOptions([]);
    mentionRequest.current += 1;
  };

  const toggleVideoLike = async () => {
    const nextLiked = !likes.liked;
    setLikes((current) => ({ liked: nextLiked, likes_count: Math.max(0, current.likes_count + (nextLiked ? 1 : -1)) }));
    try {
      const { data } = await channelApi[nextLiked ? "put" : "delete"](`/customer/shows/${showId}/like`);
      setLikes(data);
    } catch (e) {
      setLikes((current) => ({ liked: !nextLiked, likes_count: Math.max(0, current.likes_count + (nextLiked ? -1 : 1)) }));
      setError(errText(e));
    }
  };

  const toggleCommentLike = async (comment) => {
    const nextLiked = !comment.liked;
    setComments((current) => current.map((item) => item.id === comment.id ? { ...item, liked: nextLiked, likes_count: Math.max(0, item.likes_count + (nextLiked ? 1 : -1)) } : item));
    try {
      const { data } = await channelApi[nextLiked ? "put" : "delete"](`/customer/comments/${comment.id}/like`);
      setComments((current) => current.map((item) => item.id === comment.id ? { ...item, ...data } : item));
    } catch (e) {
      setComments((current) => current.map((item) => item.id === comment.id ? { ...item, liked: !nextLiked, likes_count: Math.max(0, item.likes_count + (nextLiked ? -1 : 1)) } : item));
      setError(errText(e));
    }
  };

  const deleteComment = async (comment) => {
    if (!window.confirm("Delete this comment? Its replies will remain in the thread.")) return;
    setError("");
    setNotice("");
    try {
      await channelApi.delete(`/customer/comments/${comment.id}`);
      setComments((current) => current.map((item) => item.id === comment.id
        ? { ...item, is_deleted: true, text: "", name: "Deleted comment", username: null, avatar_media_id: null, is_creator: false, is_verified: false, likes_count: 0 }
        : item));
    } catch (e) { setError(errText(e)); }
  };

  const reportComment = (comment) => {
    setReportTarget(comment);
    setReportCategory("");
    setReportDetails("");
    setReportSubmitted(false);
  };

  const submitCommentReport = async (event) => {
    event.preventDefault();
    if (!reportTarget || !reportCategory) return;
    setReportBusy(true);
    setError("");
    setNotice("");
    try {
      await channelApi.post(`/customer/comments/${reportTarget.id}/report`, {
        category: reportCategory,
        reason: reportDetails.trim(),
      });
      setReportSubmitted(true);
    } catch (e) { setError(errText(e)); }
    finally { setReportBusy(false); }
  };

  const submitComment = async (event, parentId = null) => {
    event.preventDefault();
    const text = parentId ? replyText : commentText;
    if (!text.trim()) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await channelApi.post(`/customer/shows/${showId}/comments`, { text, parent_id: parentId, mention_ids: mentionIds.map((mention) => mention.id) });
      setComments((current) => [
        ...current.map((item) => item.id === parentId ? { ...item, reply_count: (item.reply_count || 0) + 1 } : item),
        data,
      ]);
      if (parentId) {
        setReplyText("");
        setReplyTo("");
        setExpanded((current) => new Set(current).add(parentId));
      } else setCommentText("");
      setMentionIds([]);
      setMentionOptions([]);
      window.dispatchEvent(new Event("channel-notifications-refresh"));
    } catch (e) { setError(errText(e)); }
    finally { setBusy(false); }
  };

  const renderComment = (comment, depth = 0) => (
    <article key={comment.id} className="flex gap-3" data-testid={`channel-comment-${comment.id}`}>
      <Avatar mediaId={comment.avatar_media_id} name={comment.name} size={depth ? "h-8 w-8" : "h-10 w-10"} />
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-1 text-sm font-bold text-white"><span>{comment.name || "Viewer"}</span>{!comment.is_deleted && comment.is_verified && (comment.is_creator ? <CreatorBadge /> : <VerifiedCheck />)}</p>
        {!comment.is_deleted && comment.username && <p className="mt-0.5 text-xs text-white/45">@{comment.username}</p>}
        <time className="mt-0.5 block text-xs text-white/45" dateTime={comment.created_at}>{timestamp(comment.created_at)}</time>
        <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-white/80">{comment.is_deleted ? "This comment was removed." : comment.text}</p>
        {!comment.is_deleted && <div className="mt-4 flex min-h-8 flex-wrap items-center gap-x-5 gap-y-1 border-t border-white/10 pt-2 text-xs text-white/60">
          <button type="button" onClick={() => toggleCommentLike(comment)} className={`inline-flex min-h-8 items-center gap-1.5 transition-colors hover:text-white ${comment.liked ? "text-[#FF8B7F]" : ""}`} aria-pressed={Boolean(comment.liked)}>
            <ThumbsUp className="h-3.5 w-3.5" />Like{comment.likes_count > 0 ? ` · ${comment.likes_count}` : ""}
          </button>
          {!commentRestrictionActive && <button type="button" onClick={() => { setReplyTo(replyTo === comment.id ? "" : comment.id); setReplyText(""); setMentionIds([]); setMentionOptions([]); }} className="min-h-8 text-white/75 underline decoration-white/30 underline-offset-4 transition-colors hover:text-[#FF8B7F]" aria-label={`Reply to ${comment.name || "comment"}`} data-testid={`channel-comment-reply-${comment.id}`}>Reply</button>}
          {comment.can_delete && <button type="button" onClick={() => deleteComment(comment)} className="inline-flex min-h-8 items-center gap-1.5 text-white/55 transition-colors hover:text-[#FF8B7F]" aria-label="Delete comment" data-testid={`channel-comment-delete-${comment.id}`}><Trash2 className="h-3.5 w-3.5" />Delete</button>}
          {comment.can_report && <button type="button" onClick={() => reportComment(comment)} className="inline-flex min-h-8 items-center gap-1.5 text-white/55 transition-colors hover:text-[#FF8B7F]" aria-label="Report comment" data-testid={`channel-comment-report-${comment.id}`}><Flag className="h-3.5 w-3.5" />Report</button>}
        </div>}
        {!commentRestrictionActive && !comment.is_deleted && replyTo === comment.id && <form onSubmit={(event) => submitComment(event, comment.id)} className="relative mt-3 flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <textarea required maxLength={2000} rows={2} autoFocus value={replyText} onChange={(event) => updateDraft(event.target.value, comment.id)} placeholder="Write a reply..." className="w-full resize-y border border-white/20 bg-white/[0.03] px-3 py-2 text-sm leading-5 outline-none focus:border-[#E62314]" />
            {mentionOptions.length > 0 && <ul className="absolute bottom-full left-0 z-10 mb-1 w-full border border-white/15 bg-[#151515] shadow-xl" data-testid="channel-mention-suggestions">{mentionOptions.map((account) => <li key={account.id}><button type="button" onClick={() => insertMention(account, comment.id)} className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-white/[0.06]"><Avatar mediaId={account.avatar_media_id} name={account.name} size="h-8 w-8" /><span className="min-w-0 flex-1"><span className="flex items-center gap-1 font-semibold">{account.name}{account.is_verified && <VerifiedCheck />}</span><span className="block text-xs text-white/45">@{account.username}</span></span></button></li>)}</ul>}
          </div>
          <button disabled={busy || !replyText.trim()} className="border border-white/20 px-3 py-2 text-xs font-bold hover:border-[#E62314] disabled:opacity-40">Reply</button>
        </form>}
        {!comment.is_deleted && (repliesByParent[comment.id]?.length > 0 || comment.reply_count > 0) && <>
          <button type="button" onClick={() => setExpanded((current) => { const next = new Set(current); if (next.has(comment.id)) next.delete(comment.id); else next.add(comment.id); return next; })} className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#FF8B7F] hover:text-white" aria-expanded={expanded.has(comment.id)}>
            <MessageCircle className="h-3.5 w-3.5" />{expanded.has(comment.id) ? "Hide replies" : `View ${comment.reply_count || repliesByParent[comment.id]?.length || 0} replies`}
          </button>
          <div className={`grid transition-[grid-template-rows] duration-200 motion-reduce:transition-none ${expanded.has(comment.id) ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
            <div className="min-h-0 overflow-hidden">
              <div className="mt-4 space-y-5 border-l border-white/15 pl-4 sm:pl-5">
                {(repliesByParent[comment.id] || []).map((reply) => renderComment(reply, depth + 1))}
              </div>
            </div>
          </div>
        </>}
      </div>
    </article>
  );

  return (
    <section className="mt-7 border-t border-white/15 pt-5" aria-label="Video engagement" data-testid="channel-engagement">
      <button type="button" onClick={toggleVideoLike} className={`inline-flex items-center gap-2 border-b border-white/20 pb-3 text-sm font-semibold transition-colors hover:text-white ${likes.liked ? "text-[#FF8B7F]" : "text-white/65"}`} aria-pressed={likes.liked} data-testid="channel-video-like">
        <ThumbsUp className="h-4 w-4" />{likes.likes_count} {likes.likes_count === 1 ? "like" : "likes"}
      </button>
      <div className="mt-5" id="channel-comments">
        <h2 className="font-display text-xl font-bold">Comments <span className="ml-1 text-base font-semibold text-white/50">{comments.length}</span></h2>
        {commentRestrictionActive ? <p className="mt-4 border border-white/15 bg-white/[0.03] px-4 py-3 text-sm text-white/75" role="status" data-testid="channel-comment-restriction-message">
          {commentRestriction.type === "temporary"
            ? `Your ability to comment has been temporarily suspended. Your commenting access will be restored on ${restrictionTime(commentRestriction.ends_at)}.`
            : "Your ability to comment has been permanently suspended."}
        </p> : <form onSubmit={submitComment} className="mt-4 flex items-start gap-3">
          <Avatar mediaId={customer?.avatar_media_id} name="You" />
          <div className="relative min-w-0 flex-1">
            <textarea required maxLength={2000} rows={2} value={commentText} onChange={(event) => updateDraft(event.target.value)} placeholder="Add a comment..." className="w-full resize-y border border-white/20 bg-white/[0.03] px-3 py-2.5 text-sm leading-5 outline-none focus:border-[#E62314]" data-testid="channel-comment-input" />
            {mentionOptions.length > 0 && <ul className="absolute bottom-full left-0 z-10 mb-1 w-full border border-white/15 bg-[#151515] shadow-xl" data-testid="channel-mention-suggestions">{mentionOptions.map((account) => <li key={account.id}><button type="button" onClick={() => insertMention(account)} className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-white/[0.06]"><Avatar mediaId={account.avatar_media_id} name={account.name} size="h-8 w-8" /><span className="min-w-0 flex-1"><span className="flex items-center gap-1 font-semibold">{account.name}{account.is_verified && <VerifiedCheck />}</span><span className="block text-xs text-white/45">@{account.username}</span></span></button></li>)}</ul>}
            <div className="mt-2 flex justify-end"><button disabled={busy || !commentText.trim()} className="bg-[#E62314] px-4 py-2 text-xs font-bold text-white hover:bg-[#C51B10] disabled:opacity-40" data-testid="channel-comment-submit">Comment</button></div>
          </div>
        </form>}
        {error && <p className="mt-3 text-sm text-[#FF8B7F]" role="alert">{error}</p>}
        {notice && <p className="mt-3 text-sm text-green-300" role="status">{notice}</p>}
        {roots.length ? <div className="mt-6 space-y-7">{roots.map((comment) => renderComment(comment))}</div> : <p className="mt-5 text-sm text-white/50">No comments yet.</p>}
      </div>
      {reportTarget && <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/75 p-4" data-testid="channel-comment-report-dialog-backdrop">
        <section role="dialog" aria-modal="true" aria-labelledby="channel-comment-report-title" className="w-full max-w-lg border border-white/20 bg-[#111111] p-5 shadow-2xl sm:p-6" data-testid="channel-comment-report-dialog">
          {reportSubmitted ? <div className="py-2" role="status" data-testid="channel-comment-report-confirmation">
            <CheckCircle2 className="h-9 w-9 text-green-400" aria-hidden="true" />
            <h3 id="channel-comment-report-title" className="mt-4 font-display text-xl font-bold">Thank you</h3>
            <p className="mt-2 text-sm leading-6 text-white/70">Your report has been submitted for review.</p>
            <button type="button" onClick={() => { setReportTarget(null); setReportSubmitted(false); }} className="mt-5 bg-[#E62314] px-4 py-2.5 text-sm font-bold" data-testid="channel-comment-report-confirmation-close">Close</button>
          </div> : <>
            <h3 id="channel-comment-report-title" className="font-display text-xl font-bold">{customer?.is_creator && reportTarget.is_creator ? "Report your own comment" : "Report comment"}</h3>
            <p className="mt-2 line-clamp-3 whitespace-pre-wrap text-sm text-white/60">“{reportTarget.text}”</p>
            {error && <p className="mt-3 text-sm text-[#FF8B7F]" role="alert">{error}</p>}
            <form onSubmit={submitCommentReport} className="mt-5 space-y-4">
              <label className="block text-sm font-semibold">What is wrong with this comment?<select required value={reportCategory} onChange={(event) => setReportCategory(event.target.value)} className="mt-2 w-full border border-white/25 bg-[#111111] px-3 py-3 font-normal" data-testid="channel-comment-report-category"><option value="">Choose a reason</option>{reportCategories.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
              <label className="block text-sm font-semibold">Additional details <span className="font-normal text-white/45">(optional)</span><textarea maxLength={500} rows={4} value={reportDetails} onChange={(event) => setReportDetails(event.target.value)} placeholder="Tell us what happened or add context." className="mt-2 w-full resize-y border border-white/25 bg-transparent px-3 py-3 font-normal" data-testid="channel-comment-report-details" /></label>
              <div className="flex justify-end gap-2"><button type="button" disabled={reportBusy} onClick={() => setReportTarget(null)} className="border border-white/20 px-4 py-2.5 text-sm font-semibold disabled:opacity-50">Cancel</button><button type="submit" disabled={reportBusy || !reportCategory} className="bg-[#E62314] px-4 py-2.5 text-sm font-bold disabled:opacity-50" data-testid="channel-comment-report-submit">{reportBusy ? "Sending…" : "Submit report"}</button></div>
            </form>
          </>}
        </section>
      </div>}
    </section>
  );
};